import React, { useState, useEffect, useCallback, useRef } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import ServiceBreadcrumb from "../../layouts/components/ServiceBreadcrumb";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHandshake, faEye, faSearch, faFilter, faSortUp, faSortDown, faSort,
    faHeartbeat, faStethoscope, faBrain, faTooth, faBaby, faMicroscope, 
    faDumbbell, faFemale, faProcedures, faAmbulance, faCalendarCheck, faUserCheck,
    faCheckCircle, faTimesCircle, faClock, faBuilding, faFileContract, faDollarSign,
    faPaperPlane, faInbox
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import localForage from "localforage";

interface Negotiation {
    id: number;
    service_request: {
        id: number;
        title: string;
        description: string;
        status: string;
        priority: string;
        engagement_type: string;
        patient_name?: string;
        created_at: string;
        start_date?: string;
        end_date?: string;
        min_pay?: number;
        max_pay?: number;
        response_time?: number;
        creator: {
            id: number;
            name: string;
            clinic_name: string;
        };
        required_services?: string[];
    };
    application: {
        id: number;
        status: 'approved';
        proposed_rate?: number;
        proposed_timeline?: string;
        applicant: {
            id: number;
            name: string;
            clinic_name: string;
        };
    };
    negotiation_status: 'in_progress' | 'ready_for_conversion' | 'completed' | 'cancelled' | 'failed';
    negotiation_start_date: string;
    last_updated: string;
    agreed_rate?: number;
    agreed_timeline?: string;
    contract_details?: {
        contract_id: string;
        contract_status: string;
        contract_file?: string;
    };
    payment_terms?: string;
    deliverables?: string[];
    
    // Negotiation workflow fields
    current_round: number; // 1-4 rounds
    max_rounds: number; // Always 4
    sender_offers: Array<{
        round: number;
        rate: number;
        timeline: string;
        terms: string;
        timestamp: string;
    }>;
    receiver_offers: Array<{
        round: number;
        rate: number;
        timeline: string;
        terms: string;
        timestamp: string;
    }>;
    last_offer_from: 'sender' | 'receiver';
    conversion_deadline: string; // When negotiation must be converted to collaboration
    is_ready_for_conversion: boolean;
}

const Negotiations: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    
    // Filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [directionFilter, setDirectionFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
    const [sortField, setSortField] = useState<keyof Negotiation>('negotiation_start_date');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    // Infinite scroll ref
    const observer = useRef<IntersectionObserver | undefined>(undefined);
    const lastNegotiationRef = useRef<HTMLDivElement | null>(null);

    // Last negotiation element callback for infinite scroll
    const lastNegotiationCallback = useCallback((node: HTMLDivElement) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        document.title = "Negotiations - Uzima Healthcare";
        // Get clinic ID from localStorage
        localForage.getItem("clinic_id").then((clin: any) => {
            setClinicId(clin);
        });
    }, []);

    useEffect(() => {
        if (clinicId) {
            fetchNegotiations(true); // Reset and fetch first page
        }
    }, [clinicId, searchTerm, statusFilter, priorityFilter, directionFilter, sortField, sortDirection]);

    // Check for expired conversion deadlines and update status
    useEffect(() => {
        if (negotiations.length > 0) {
            const checkExpiredDeadlines = async () => {
                const expiredNegotiations = negotiations.filter(neg => 
                    neg.conversion_deadline && 
                    neg.negotiation_status === 'ready_for_conversion' &&
                    isDeadlineExpired(neg.conversion_deadline)
                );

                if (expiredNegotiations.length > 0) {
                    // Update expired negotiations to failed status
                    for (const negotiation of expiredNegotiations) {
                        try {
                            await fetch(`${baseUrl}collaborations/negotiations/${negotiation.id}/expire/`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: 'failed' })
                            });
                        } catch (error) {
                            console.error(`Failed to update expired negotiation ${negotiation.id}:`, error);
                        }
                    }
                    
                    // Refresh negotiations list
                    fetchNegotiations(true);
                }
            };

            checkExpiredDeadlines();
        }
    }, [negotiations, baseUrl]);

    useEffect(() => {
        if (page > 1) {
            fetchNegotiations(false); // Fetch additional pages
        }
    }, [page]);

    const fetchNegotiations = async (reset: boolean = false) => {
        if (!clinicId) return;
        
        try {
            setLoading(true);
            const currentPage = reset ? 1 : page;
            
            const params = new URLSearchParams({
                page: currentPage.toString(),
                page_size: pageSize.toString(),
                search: searchTerm,
                status: statusFilter,
                priority: priorityFilter,
                direction: directionFilter,
                sort_by: sortField,
                sort_direction: sortDirection,
                clinic_id: clinicId.toString()
            });

            const response = await fetch(`${baseUrl}collaborations/negotiations/?${params}`);
            if (response.ok) {
                const data = await response.json();
                const newNegotiations = data.data?.negotiations || [];
                
                if (reset) {
                    setNegotiations(newNegotiations);
                    setPage(1);
                } else {
                    setNegotiations(prev => [...prev, ...newNegotiations]);
                }
                
                setHasMore(newNegotiations.length === pageSize);
            } else {
                console.error('Failed to fetch negotiations');
                if (reset) setNegotiations([]);
            }
        } catch (error) {
            console.error('Error fetching negotiations:', error);
            if (reset) setNegotiations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: keyof Negotiation) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setPriorityFilter('');
        setDirectionFilter('all');
        setSortField('negotiation_start_date');
        setSortDirection('desc');
    };

    const getRequestIcon = (title: string, engagementType: string) => {
        const lowerTitle = title.toLowerCase();
        
        if (lowerTitle.includes('cardio') || lowerTitle.includes('heart')) return faHeartbeat;
        if (lowerTitle.includes('consultation') || lowerTitle.includes('checkup')) return faStethoscope;
        if (lowerTitle.includes('neuro') || lowerTitle.includes('brain')) return faBrain;
        if (lowerTitle.includes('dental') || lowerTitle.includes('tooth')) return faTooth;
        if (lowerTitle.includes('pediatric') || lowerTitle.includes('child')) return faBaby;
        if (lowerTitle.includes('lab') || lowerTitle.includes('test')) return faMicroscope;
        if (lowerTitle.includes('physio') || lowerTitle.includes('therapy')) return faDumbbell;
        if (lowerTitle.includes('gyneco') || lowerTitle.includes('women')) return faFemale;
        if (lowerTitle.includes('surgery') || lowerTitle.includes('operation')) return faProcedures;
        if (lowerTitle.includes('emergency') || lowerTitle.includes('urgent')) return faAmbulance;
        if (lowerTitle.includes('appointment') || lowerTitle.includes('schedule')) return faCalendarCheck;
        
        return faStethoscope;
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'in_progress': return 'warning';
            case 'ready_for_conversion': return 'info';
            case 'completed': return 'success';
            case 'cancelled': return 'danger';
            case 'failed': return 'dark';
            default: return 'secondary';
        }
    };

    const isDeadlineExpired = (deadline: string) => {
        return new Date(deadline) < new Date();
    };

    const isDeadlineClose = (deadline: string) => {
        const deadlineDate = new Date(deadline);
        const now = new Date();
        const diffTime = deadlineDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 3 && diffDays > 0; // Within 3 days
    };

    const handleConvertToCollaboration = async (negotiationId: number) => {
        try {
            // Navigate to collaboration creation page with negotiation data
            navigate(`/institution/collaborations/create?from_negotiation=${negotiationId}`);
        } catch (error) {
            console.error('Error converting to collaboration:', error);
        }
    };

    const handlePlaceCounterOffer = async (negotiationId: number) => {
        try {
            // Navigate to counter offer page
            navigate(`/institution/services/negotiation-counter-offer/${negotiationId}`);
        } catch (error) {
            console.error('Error placing counter offer:', error);
        }
    };

    const getNegotiationProgress = (negotiation: Negotiation) => {
        if (!negotiation.current_round || !negotiation.max_rounds) return 0;
        return Math.round((negotiation.current_round / negotiation.max_rounds) * 100);
    };

    const isInboundNegotiation = (negotiation: Negotiation) => {
        return negotiation.service_request.creator.id !== clinicId;
    };

    const isOutboundNegotiation = (negotiation: Negotiation) => {
        return negotiation.service_request.creator.id === clinicId;
    };

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'low': return 'success';
            case 'normal': return 'info';
            case 'high': return 'warning';
            case 'urgent': return 'danger';
            default: return 'secondary';
        }
    };

    const getNegotiationStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case 'in_progress': return faClock;
            case 'ready_for_conversion': return faCheckCircle;
            case 'completed': return faCheckCircle;
            case 'cancelled': return faTimesCircle;
            case 'failed': return faTimesCircle;
            default: return faClock;
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        <ServiceBreadcrumb currentPage="Negotiations" />
                        <div className="row mb-4 align-items-center">
                            <div className="col-8">
                                <h2 className="fw-bold mb-0">
                                    <FontAwesomeIcon icon={faHandshake} className="me-2 text-primary" />
                                    Negotiations
                                </h2>
                                <div className="text-muted">Accepted service requests ready to become collaborations</div>
                            </div>
                            <div className="col-4 text-end">
                                <span className="badge bg-primary fs-6">
                                    {negotiations.length} negotiations
                                </span>
                            </div>
                        </div>

                        {/* Negotiation Statistics */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-2">
                                <div className="card border-0 bg-primary text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'in_progress').length}</div>
                                        <small>In Progress</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 bg-info text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'ready_for_conversion').length}</div>
                                        <small>Ready to Convert</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 bg-success text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'completed').length}</div>
                                        <small>Completed</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 bg-warning text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'cancelled').length}</div>
                                        <small>Cancelled</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 bg-dark text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'failed').length}</div>
                                        <small>Failed</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 bg-secondary text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.length}</div>
                                        <small>Total</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Direction Statistics */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <div className="card border-0 bg-info text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => isInboundNegotiation(n)).length}</div>
                                        <small>Inbound Negotiations</small>
                                        <div className="small mt-1">Requests you received & accepted</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card border-0 bg-warning text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => isOutboundNegotiation(n)).length}</div>
                                        <small>Outbound Negotiations</small>
                                        <div className="small mt-1">Requests you sent & got accepted</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card border-0 bg-secondary text-white">
                                    <div className="card-body text-center p-3">
                                        <div className="h4 mb-1">{negotiations.filter(n => n.negotiation_status === 'ready_for_conversion').length}</div>
                                        <small>Ready for Conversion</small>
                                        <div className="small mt-1">Agreements reached</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FontAwesomeIcon icon={faSearch} />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search negotiations..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="ready_for_conversion">Ready for Conversion</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="failed">Failed</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            value={priorityFilter}
                                            onChange={(e) => setPriorityFilter(e.target.value)}
                                        >
                                            <option value="">All Priorities</option>
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Direction</label>
                                        <select
                                            className="form-select"
                                            value={directionFilter}
                                            onChange={(e) => setDirectionFilter(e.target.value as 'all' | 'inbound' | 'outbound')}
                                        >
                                            <option value="all">All Directions</option>
                                            <option value="inbound">Inbound</option>
                                            <option value="outbound">Outbound</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <button
                                            className="btn btn-outline-secondary w-100"
                                            onClick={clearFilters}
                                        >
                                            <FontAwesomeIcon icon={faFilter} className="me-1" />
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Negotiations Grid */}
                        <div className="row g-4">
                            {negotiations
                                .filter(negotiation => {
                                    if (directionFilter === 'inbound') return isInboundNegotiation(negotiation);
                                    if (directionFilter === 'outbound') return isOutboundNegotiation(negotiation);
                                    return true; // 'all' - show all
                                })
                                .map((negotiation, index) => (
                                <div 
                                    key={negotiation.id} 
                                    className="col-lg-6 col-xl-4"
                                    ref={index === negotiations.length - 1 ? lastNegotiationCallback : undefined}
                                >
                                    <div 
                                        className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                                        style={{ 
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                        }}
                                        onClick={() => navigate(`/institution/services/negotiation-details/${negotiation.id}`)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {/* Negotiation Status Badge */}
                                        <div className="position-absolute top-0 end-0 p-3">
                                            <span className={`badge bg-${getStatusColor(negotiation.negotiation_status)} fs-6 px-3 py-2`}>
                                                <FontAwesomeIcon 
                                                    icon={getNegotiationStatusIcon(negotiation.negotiation_status)} 
                                                    className="me-1" 
                                                />
                                                {negotiation.negotiation_status}
                                            </span>
                                        </div>

                                        {/* Direction Badge */}
                                        <div className="position-absolute top-0 start-0 p-3">
                                            <span className={`badge ${negotiation.service_request.creator.id === clinicId ? 'bg-info' : 'bg-warning'} fs-6 px-3 py-2`}>
                                                <FontAwesomeIcon 
                                                    icon={negotiation.service_request.creator.id === clinicId ? faPaperPlane : faInbox} 
                                                    className="me-1" 
                                                />
                                                {negotiation.service_request.creator.id === clinicId ? 'Outbound' : 'Inbound'}
                                            </span>
                                        </div>

                                        {/* Request Priority Badge */}
                                        <div className="position-absolute top-0 start-0 p-3" style={{ marginTop: '60px' }}>
                                            <span className={`badge bg-${getPriorityColor(negotiation.service_request.priority)} fs-6 px-3 py-2`}>
                                                {negotiation.service_request.priority}
                                            </span>
                                        </div>

                                        <div className="card-body p-4">
                                            {/* Icon and Title */}
                                            <div className="text-center mb-4">
                                                <div 
                                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
                                                        color: 'white',
                                                        boxShadow: '0 8px 25px rgba(132, 250, 176, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={getRequestIcon(negotiation.service_request.title, negotiation.service_request.engagement_type)} 
                                                        size="2x"
                                                    />
                                                </div>
                                                <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                                    {negotiation.service_request.title}
                                                </h5>
                                            </div>

                                            {/* Description */}
                                            <p className="card-text text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                {negotiation.service_request.description.length > 100 
                                                    ? `${negotiation.service_request.description.substring(0, 100)}...` 
                                                    : negotiation.service_request.description
                                                }
                                            </p>

                                            {/* Engagement Type */}
                                            <div className="text-center mb-3">
                                                <span className="badge bg-info fs-6 px-3 py-2">
                                                    {negotiation.service_request.engagement_type}
                                                </span>
                                            </div>

                                            {/* Request Creator Info */}
                                            <div className="text-center mb-3">
                                                <small className="text-muted d-block">
                                                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                                    Request Creator
                                                </small>
                                                <div className="fw-bold text-dark">
                                                    {negotiation.service_request.creator.name}
                                                </div>
                                                <small className="text-muted">
                                                    {negotiation.service_request.creator.clinic_name}
                                                </small>
                                            </div>

                                            {/* Applicant Info */}
                                            <div className="text-center mb-3">
                                                <small className="text-muted d-block">
                                                    <FontAwesomeIcon icon={faUserCheck} className="me-1" />
                                                    Service Provider
                                                </small>
                                                <div className="fw-bold text-dark">
                                                    {negotiation.application.applicant.name}
                                                </div>
                                                <small className="text-muted">
                                                    {negotiation.application.applicant.clinic_name}
                                                </small>
                                            </div>

                                            {/* Agreed Rate */}
                                            {negotiation.agreed_rate && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">
                                                        <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                                                        Agreed Rate
                                                    </small>
                                                    <div className="fw-bold text-success">
                                                        Ksh {negotiation.agreed_rate.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Proposed Rate */}
                                            {negotiation.application.proposed_rate && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Originally Proposed</small>
                                                    <div className="fw-bold text-info">
                                                        Ksh {negotiation.application.proposed_rate.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Contract Status */}
                                            {negotiation.contract_details && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">
                                                        <FontAwesomeIcon icon={faFileContract} className="me-1" />
                                                        Contract Status
                                                    </small>
                                                    <div className="fw-bold text-primary">
                                                        {negotiation.contract_details.contract_status}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Patient Info */}
                                            {negotiation.service_request.patient_name && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted">
                                                        Patient: {negotiation.service_request.patient_name}
                                                    </small>
                                                </div>
                                            )}

                                            {/* Dates */}
                                            <div className="row text-center mb-3">
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Negotiation Started</small>
                                                    <small className="fw-bold text-dark">
                                                        {new Date(negotiation.negotiation_start_date).toLocaleDateString()}
                                                    </small>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Last Updated</small>
                                                    <small className="fw-bold text-dark">
                                                        {new Date(negotiation.last_updated).toLocaleDateString()}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Negotiation Round Information */}
                                            <div className="text-center mb-3">
                                                <small className="text-muted d-block">
                                                    <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                                    Negotiation Round
                                                </small>
                                                <div className="fw-bold text-primary">
                                                    Round {negotiation.current_round || 1} of {negotiation.max_rounds || 4}
                                                </div>
                                                
                                                {/* Progress Bar */}
                                                <div className="progress mt-2" style={{ height: '8px' }}>
                                                    <div 
                                                        className="progress-bar bg-primary" 
                                                        style={{ width: `${getNegotiationProgress(negotiation)}%` }}
                                                        role="progressbar"
                                                    />
                                                </div>
                                                <small className="text-muted">
                                                    {getNegotiationProgress(negotiation)}% Complete
                                                </small>
                                                
                                                {negotiation.last_offer_from && (
                                                    <small className="text-muted d-block mt-1">
                                                        Last offer from: {negotiation.last_offer_from === 'sender' ? 'Request Creator' : 'Service Provider'}
                                                    </small>
                                                )}
                                            </div>

                                            {/* Conversion Deadline */}
                                            {negotiation.conversion_deadline && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">
                                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                                        Conversion Deadline
                                                    </small>
                                                    <div className={`fw-bold ${isDeadlineExpired(negotiation.conversion_deadline) ? 'text-danger' : 'text-warning'}`}>
                                                        {new Date(negotiation.conversion_deadline).toLocaleDateString()}
                                                    </div>
                                                    {isDeadlineExpired(negotiation.conversion_deadline) && (
                                                        <small className="text-danger">
                                                            ⚠️ Deadline expired - Convert now or risk failure
                                                        </small>
                                                    )}
                                                    {!isDeadlineExpired(negotiation.conversion_deadline) && isDeadlineClose(negotiation.conversion_deadline) && (
                                                        <small className="text-warning">
                                                            ⏰ Deadline approaching - Convert soon
                                                        </small>
                                                    )}
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="text-center">
                                                {negotiation.negotiation_status === 'ready_for_conversion' && (
                                                    <div className="d-grid gap-2 mb-2">
                                                        <button 
                                                            className="btn btn-success btn-sm px-4 py-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleConvertToCollaboration(negotiation.id);
                                                            }}
                                                            style={{
                                                                borderRadius: '25px',
                                                                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                                            Convert to Collaboration
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {negotiation.negotiation_status === 'in_progress' && (
                                                    <div className="d-grid gap-2 mb-2">
                                                        <button 
                                                            className="btn btn-warning btn-sm px-4 py-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePlaceCounterOffer(negotiation.id);
                                                            }}
                                                            style={{
                                                                borderRadius: '25px',
                                                                boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                            Place Counter Offer
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                <button 
                                                    className="btn btn-primary btn-sm px-4 py-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/institution/services/negotiation-details/${negotiation.id}`);
                                                    }}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
                                                        border: 'none',
                                                        borderRadius: '25px',
                                                        boxShadow: '0 4px 15px rgba(132, 250, 176, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="me-2" />
                                                    View Details
                                                </button>
                                            </div>
                                        </div>

                                        {/* Decorative Elements */}
                                        <div 
                                            className="position-absolute bottom-0 start-0 w-100 h-100"
                                            style={{
                                                background: 'linear-gradient(135deg, transparent 0%, rgba(132, 250, 176, 0.05) 100%)',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Loading Indicator */}
                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-2 text-muted">Loading more negotiations...</div>
                            </div>
                        )}

                        {/* No More Data */}
                        {!hasMore && negotiations.length > 0 && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <FontAwesomeIcon icon={faHandshake} size="2x" className="mb-3" />
                                    <h6>No more negotiations to load</h6>
                                    <p>You've reached the end of the negotiations.</p>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && negotiations.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faHandshake} size="3x" className="mb-3 text-primary" />
                                <h5>No negotiations found</h5>
                                <p className="text-muted">No negotiations match your current filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    );
};

export default Negotiations;
